import type { ReactNode } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, IconButton, Icon } from "@visioni/a3s-ui/components";

export interface CompilationWatchViewProps {
  /** Camera display name — the header's main title. */
  readonly cameraLabel: string;
  /** Requested date/time range, e.g. "1 Aug 2026, 2:00pm – 3 Aug 2026, 2:00pm" — shown as a subheader beneath the title. */
  readonly dateRange: string;
  /** Omit when the host screen's own header already provides a back
   * affordance (e.g. native's Stack header) — avoids showing two back buttons. */
  readonly onBack?: (() => void) | undefined;
  /** Omit to disable the download button (e.g. no download URL is available yet). */
  readonly onDownload?: (() => void) | undefined;
  /** The actual video player — engine-specific (`expo-video`, a plain `<video>`,
   * etc.), so it's supplied by the consuming app rather than owned here. */
  readonly children: ReactNode;
}

/**
 * Full-page layout for watching a compilation: a header (back, title +
 * subheader, download) over the player area the caller supplies. Kept here
 * rather than in the app so the layout is shared across native/web the same
 * way the rest of the clip-compilation UI is — only the player itself is
 * app-specific.
 */
export function CompilationWatchView({
  cameraLabel,
  dateRange,
  onBack,
  onDownload,
  children,
}: CompilationWatchViewProps) {
  const { theme } = useUnistyles();

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {onBack && (
          <IconButton
            Icon={() => <Icon name="arrowLeft" size={20} color={theme.colors.text} />}
            title="Back to compilations"
            size="sm"
            onPress={onBack}
          />
        )}
        <View style={styles.headerTitle}>
          <Text textStyle="md/medium" color="text" numberOfLines={1}>
            {cameraLabel}
          </Text>
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            {dateRange}
          </Text>
        </View>
        <IconButton
          Icon={() => <Icon name="download" size={16} color={theme.colors.text} />}
          title="Download compilation"
          size="sm"
          disabled={!onDownload}
          {...(onDownload ? { onPress: onDownload } : {})}
        />
      </View>
      <View style={styles.player}>{children}</View>
    </View>
  );
}

CompilationWatchView.displayName = "CompilationWatchView";

const styles = StyleSheet.create((theme) => ({
  container: {
    flex: 1,
    padding: theme.spacing.xl,
    gap: theme.spacing.xl,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  headerTitle: {
    flex: 1,
    gap: theme.spacing.xxs,
  },
  player: {
    flex: 1,
  },
}));
